<template>
  <v-app class="referral-wow-layout">
    <div class="ambient-glow glow-primary"></div>
    <div class="ambient-glow glow-secondary"></div>

    <v-overlay :value="isLoading" absolute color="#020202" opacity="1" z-index="100">
      <v-progress-circular indeterminate color="primary" size="80" width="4"></v-progress-circular>
    </v-overlay>

    <v-container v-if="!isLoading && !isActive" class="fill-height justify-center relative-z">
      <div class="glass-card pa-12 rounded-xl text-center inactive-box">
        <div class="icon-wrapper mx-auto mb-6">
          <v-icon size="60" color="grey darken-1">mdi-lock-outline</v-icon>
        </div>
        <h2 class="text-h4 font-weight-black text-uppercase mb-4 white--text">Aktion Beendet</h2>
        <p class="text-body-1 white--text text--lighten-1 mb-10 mx-auto" style="max-width: 350px">
          Das "Bring a Friend"-Programm ist für {{ studio ? studio.art || studio.name : "dein Studio" }} aktuell nicht verfügbar. Schau bald wieder
          vorbei!
        </p>
        <v-btn color="primary" x-large rounded outlined class="font-weight-bold px-10 btn-hover-glow" @click="goHome"> Zurück zur Startseite </v-btn>
      </div>
    </v-container>

    <v-row v-else-if="!isLoading && isActive" no-gutters class="fill-height split-layout relative-z">
      <v-col
        cols="12"
        md="5"
        lg="4"
        :class="['hero-panel flex-column justify-center align-center text-center pa-8 pa-md-12', isAccepted ? 'd-none d-md-flex' : 'd-flex']">
        <div class="hero-content w-100">
          <v-img
            v-if="studio && studio.code"
            :src="`https://app.wellcomefit.ch/img/logos/studio/WELL-COME-FIT_LOGO_${studio.code.toUpperCase()}_BLACK.png`"
            max-width="180"
            contain
            class="mx-auto mb-12 logo-pop"></v-img>

          <h1 class="text-h4 text-md-h3 font-weight-black text-uppercase white--text tracking-tight mb-2 text-gradient">Bring a Friend</h1>
          <p class="text-subtitle-1 white--text text--lighten-1 font-weight-regular mb-10 tracking-widest text-uppercase">Empfehle uns & Kassiere</p>

          <div class="price-glass-card mx-auto">
            <div class="glow-border"></div>
            <div class="price-content">
              <div class="currency-wrapper d-flex flex-column align-end mr-3">
                <v-icon color="primary" size="24" class="mb-1">mdi-cash-multiple</v-icon>
                <span class="currency">CHF</span>
              </div>
              <span class="amount">{{ rewardAmount }}.-</span>
            </div>
          </div>
        </div>
      </v-col>

      <v-col cols="12" md="7" lg="8" class="content-panel d-flex align-center pa-6 pa-md-12 pa-lg-16">
        <div class="content-wrapper mx-auto w-100" style="max-width: 650px">
          <transition name="scale-fade" mode="out-in">
            <div v-if="!isAccepted" key="info-step">
              <h2 class="text-h3 font-weight-black white--text mb-10 tracking-tight line-height-tight">
                Deine Empfehlung <br />
                <span class="primary--text">verdient ein Dankeschön.</span>
              </h2>

              <div class="feature-list mb-12">
                <div class="feature-item d-flex align-start mb-6">
                  <div class="feature-icon-box mr-5 mt-1">
                    <v-icon color="primary" size="28">mdi-star-shooting-outline</v-icon>
                  </div>
                  <div>
                    <div class="d-flex align-center flex-wrap mb-1" style="column-gap: 10px; row-gap: 4px">
                      <h4 class="text-h6 white--text font-weight-bold mb-0">CHF {{ rewardAmount }} pro Empfehlung</h4>
                      <span class="extended-tag">VERLÄNGERT</span>
                    </div>
                    <p class="white--text text--lighten-1 mb-0 text-body-1">
                      <span v-if="referralPromoActive">Aktion verlängert bis Ende Mai – f</span><span v-else>F</span>ür jede erfolgreiche
                      Weiterempfehlung bedanken wir uns direkt bei dir.
                    </p>
                  </div>
                </div>

                <div class="feature-item d-flex align-start mb-6">
                  <div class="feature-icon-box mr-5 mt-1">
                    <v-icon color="white" size="28">mdi-trending-up</v-icon>
                  </div>
                  <div>
                    <h4 class="text-h6 white--text font-weight-bold mb-1">Höchster Stand seit 35 Jahren</h4>
                    <p class="white--text text--darken-1 mb-0 text-body-1">
                      Unsere Mitgliederzufriedenheit ist um über 10% gestiegen. Das zeigt: Ihr fühlt euch wohl!
                    </p>
                  </div>
                </div>

                <div class="feature-item d-flex align-start">
                  <div class="feature-icon-box mr-5 mt-1">
                    <v-icon color="white" size="28">mdi-account-heart-outline</v-icon>
                  </div>
                  <div>
                    <h4 class="text-h6 white--text font-weight-bold mb-1">Von Freunden für Freunde</h4>
                    <p class="white--text text--darken-1 mb-0 text-body-1">
                      Viele neue Mitglieder kommen durch euch. Genau dafür möchten wir jetzt etwas zurückgeben.
                    </p>
                  </div>
                </div>
              </div>

              <div class="conditions-glass pa-5 rounded-lg mb-10 d-flex align-start">
                <v-icon color="grey darken-2" size="20" class="mr-3 mt-1">mdi-information-outline</v-icon>
                <p class="text-caption white--text text--darken-1 mb-0 lh-relaxed">
                  Angebot nicht kumulierbar / kein Firmenrabatt zusätzlich / nach Abonnementkündigung verfällt die Gutschrift / keine Barauszahlung
                  des Betrags
                </p>
              </div>

              <div class="action-grid mt-8 mobile-sticky-actions">
                <v-btn
                  color="primary"
                  x-large
                  block
                  elevation="0"
                  class="font-weight-black text-uppercase action-btn pulse-glow mb-4"
                  style="height: 64px; border-radius: 16px"
                  @click="isAccepted = true">
                  <span class="text-h6">Link generieren</span>
                </v-btn>
                <v-btn text x-large block color="grey darken-1" class="font-weight-bold action-btn" style="border-radius: 16px" @click="goHome">
                  Vielleicht Später
                </v-btn>
              </div>
            </div>

            <div v-else key="share-step" class="share-wrapper text-center text-md-left">
              <div class="d-flex flex-column flex-md-row align-center align-md-start mb-10 pt-8 pt-md-0">
                <div class="success-icon-container mb-6 mb-md-0 mr-md-8">
                  <div class="success-glow"></div>
                  <v-icon color="primary" size="50">mdi-check-decagram-outline</v-icon>
                </div>
                <div>
                  <h2 class="text-h3 font-weight-black white--text mb-3 tracking-tight">Dein Link ist <span class="primary--text">bereit!</span></h2>
                  <p class="text-h6 white--text text--lighten-1 mb-0 lh-relaxed font-weight-regular">
                    Teile diesen exklusiven Link. Sobald sich jemand anmeldet, sichern wir dir deine Prämie.
                  </p>
                </div>
              </div>

              <div class="link-copier-glass pa-2 pl-6 rounded-pill mb-12 d-flex align-center justify-space-between">
                <v-icon color="primary" size="24" class="mr-4">mdi-link-variant</v-icon>
                <div class="text-truncate flex-grow-1 white--text font-weight-bold text-h6 text-left mr-4">
                  {{ refUrl }}
                </div>
                <v-btn color="primary" x-large rounded depressed class="px-8 font-weight-black copy-btn" @click="copyUrl"> Kopieren </v-btn>
              </div>

              <div class="divider-wrapper mb-10">
                <span class="divider-text text-uppercase font-weight-bold tracking-widest white--text text--darken-1">Direkt Teilen</span>
              </div>

              <div class="social-bento-grid">
                <div class="bento-item whatsapp" @click="shareSocial('whatsapp')">
                  <v-icon size="36" color="white" class="mb-2">mdi-whatsapp</v-icon>
                  <span>WhatsApp</span>
                </div>
                <div class="bento-item telegram" @click="shareSocial('telegram')">
                  <v-icon size="36" color="white" class="mb-2">mdi-send</v-icon>
                  <span>Telegram</span>
                </div>
                <div class="bento-item facebook" @click="shareSocial('facebook')">
                  <v-icon size="36" color="white" class="mb-2">mdi-facebook</v-icon>
                  <span>Facebook</span>
                </div>
                <div class="bento-item email" @click="shareSocial('email')">
                  <v-icon size="36" color="white" class="mb-2">mdi-email-outline</v-icon>
                  <span>E-Mail</span>
                </div>
              </div>

              <v-btn
                v-if="canShare"
                block
                x-large
                color="#1a1a1a"
                class="native-share-btn white--text mt-4 font-weight-bold"
                style="border-radius: 16px; border: 1px solid rgba(255, 255, 255, 0.1)"
                @click="shareNative"
                depressed>
                <v-icon left size="24" color="primary">mdi-share-variant-outline</v-icon> Weitere Optionen
              </v-btn>

              <div class="mt-12 text-center text-md-left">
                <v-btn text rounded color="grey darken-1" class="font-weight-bold" @click="goHome">
                  <v-icon left>mdi-arrow-left</v-icon> Zurück zur Startseite
                </v-btn>
              </div>
            </div>
          </transition>
        </div>
      </v-col>
    </v-row>
    <v-dialog v-model="showLoginPrompt" persistent max-width="440" overlay-opacity="0.85">
      <v-card class="login-prompt" dark>
        <div class="login-prompt__glow"></div>

        <div class="login-prompt__content pa-6 text-center">
          <div class="login-prompt__icon mb-4">
            <v-img
              :height="'120px'"
              :width="'120px'"
              contain
              class="mx-auto"
              position="center center"
              :src="`https://app.wellcomefit.ch/img/logos/studio/WELL-COME-FIT_LOGO_BLACK.png`"></v-img>
          </div>

          <h2 class="login-prompt__title mb-2">Bitte einloggen</h2>
          <p class="login-prompt__subtitle mb-6">Melde dich mit deinem App Account an, um von deinem Referral-Vorteil zu profitieren 🚀</p>

          <v-btn x-large rounded block color="primary" class="login-prompt__btn-login mb-3" @click="showLoginDialog = true"> anmelden </v-btn>

          <v-btn x-large rounded block outlined color="white" class="mb-3" @click="goToLogin"> registrieren </v-btn>

          <span class="login-prompt__forgot" @click="goToLogin"> Passwort vergessen? </span>
        </div>
      </v-card>
    </v-dialog>

    <login @login="accountLogin" :auto-login="false" @close="showLoginDialog = false" :active="showLoginDialog"></login>
  </v-app>
</template>

<script>
import api from "../../../api"
import { mapActions } from "vuex"
import { EventBus } from "@/event-bus"
import login from "@/views/launchScreen/login.vue"
export default {
  components: { login },
  data() {
    return {
      showLoginPrompt: false,
      showLoginDialog: false,
      isLoading: true,
      isActive: false,
      isAccepted: false,
      reward: 50,
      studio: null,
      user: {},
      host: null,
    }
  },
  async created() {
    this.loadReferralData()
  },
  beforeDestroy() {
    this.setNavigationbarVisibility(true)
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["setNavigationbarVisibility"]),
    ...mapActions("userStore", ["setUserRights", "setUser"]),
    async loadReferralData() {
      this.host = window.location.host.replace("www.", "")
      this.setNavigationbarVisibility(false)
      try {
        const res = await api.getUser()
        if (res && res.user) {
          this.user = res.user
        } else {
          // Hier wirfst du den Fehler
          throw new Error("User not found in response")
        }
        await this.fetchData()
      } catch (e) {
        console.error(e) // Optional: Logge den genauen Fehler für dich
        this.showLoginPrompt = true
        this.isLoading = false
      }
    },
    async fetchData() {
      try {
        const res = await api.getUserStudio()
        if (res.studio) {
          this.studio = res.studio
          if (res.studio.leadSetting && res.studio.leadSetting.active) {
            this.isActive = true
            if (res.studio.leadSetting.price && res.studio.leadSetting.price.value) {
              this.reward = res.studio.leadSetting.price.value
            }
          }
        }
      } catch (e) {
        console.error("Referral fetch error:", e)
      } finally {
        this.isLoading = false
      }
    },

    async accountLogin(loginData) {
      localStorage.setItem("token", loginData.token) // 👈 sofort setzen
      EventBus.$emit("handleAuthenticationSuccess", loginData.token)

      this.showLoginDialog = false
      this.showLoginPrompt = false
      this.isLoading = true
      await this.$nextTick()
      await this.loadReferralData()
    },

    goToLogin() {
      this.$router.push("/login")
    },

    goHome() {
      this.$router.push("/")
    },

    copyUrl() {
      navigator.clipboard.writeText(this.refUrl)
      this.$toast.success("Link erfolgreich kopiert! ")
    },

    shareSocial(app) {
      const msg = encodeURIComponent("Komm mit mir ins Training!")
      const link = encodeURIComponent(this.refUrl)
      let target = ""

      switch (app) {
        case "whatsapp":
          target = `https://api.whatsapp.com/send?text=${msg}%20${link}`
          break
        case "facebook":
          target = `https://www.facebook.com/sharer/sharer.php?u=${link}`
          break
        case "telegram":
          target = `https://t.me/share/url?url=${link}&text=${msg}`
          break
        case "email":
          target = `mailto:?subject=Empfehlung&body=${msg}%0A%0A${this.refUrl}`
          break
      }
      if (target) window.open(target, "_blank", "noopener,noreferrer")
    },

    shareNative() {
      if (this.canShare) {
        navigator.share({
          title: "Bring a Friend - " + (this.studio?.art || "Well Come FIT"),
          text: "Komm mit mir ins Training!",
          url: this.refUrl,
        })
      }
    },
  },
  computed: {
    referralPromoActive() {
      const now = this.$moment()
      return now.month() === 4 && now.year() === 2026
    },
    refUrl() {
      return this.user.email && this.host ? `https://${this.host}/r/${this.user.email.toLowerCase()}` : ""
    },
    rewardAmount() {
      return this.reward
    },
    canShare() {
      return !!navigator.share
    },
  },
}
</script>

<style lang="scss" scoped>
.extended-tag {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: var(--v-primary-base);
  text-transform: uppercase;
  white-space: nowrap;
  text-shadow: 0 0 8px rgba(var(--v-primary-base-rgb), 0.5);

  &::before {
    content: "";
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--v-primary-base);
    margin-right: 6px;
    box-shadow: 0 0 6px var(--v-primary-base), 0 0 12px rgba(var(--v-primary-base-rgb), 0.6);
    animation: dotBlink 1.5s ease-in-out infinite;
  }
}

@keyframes dotBlink {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(0.85);
  }
}
.login-prompt {
  position: relative;
  overflow: hidden;
  border-radius: 24px !important;
  background: linear-gradient(160deg, #1a1a1a 0%, #0d0d0d 100%) !important;
  border: 1px solid rgba(255, 255, 255, 0.08);

  &__glow {
    position: absolute;
    top: -80px;
    left: 50%;
    transform: translateX(-50%);
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, var(--v-primary-base) 0%, transparent 70%);
    opacity: 0.35;
    filter: blur(40px);
    pointer-events: none;
  }

  &__content {
    position: relative;
    z-index: 1;
  }

  &__title {
    font-size: 26px;
    font-weight: 800;
    color: #fff;
    letter-spacing: 0.5px;
  }

  &__subtitle {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
  }

  &__btn-login {
    background-image: linear-gradient(0deg, var(--v-primary-base) 0%, var(--v-darkenprimary-base) 80%, var(--v-darkenprimary2-base) 100%);
    text-transform: uppercase;
    font-weight: 700;
  }

  &__forgot {
    display: inline-block;
    margin-top: 8px;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.6);
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: #fff;
      text-decoration: underline;
    }
  }
}
/* Core Setup */
.referral-wow-layout {
  background-color: #020202 !important;
  font-family: "Inter", "Roboto", sans-serif;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
}

.relative-z {
  position: relative;
  z-index: 10;
}

/* Typography Utilities */
.tracking-tight {
  letter-spacing: -0.04em !important;
}
.tracking-widest {
  letter-spacing: 0.2em !important;
}
.lh-relaxed {
  line-height: 1.6 !important;
}
.line-height-tight {
  line-height: 1.1 !important;
}

/* Cinematic Ambient Glows */
.ambient-glow {
  position: fixed;
  border-radius: 50%;
  filter: blur(120px);
  z-index: 1;
  opacity: 0.15;
  pointer-events: none;
}
.glow-primary {
  width: 600px;
  height: 600px;
  background: var(--v-primary-base);
  top: -200px;
  left: -200px;
  animation: float 10s ease-in-out infinite alternate;
}
.glow-secondary {
  width: 500px;
  height: 500px;
  background: #ffffff;
  bottom: -100px;
  right: -100px;
  opacity: 0.05;
}

@keyframes float {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(50px, 50px);
  }
}

/* Glassmorphism Basics */
.glass-card {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.5);
}

/* HERO LEFT PANEL */
.hero-panel {
  background: transparent;
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  @media (min-width: 960px) {
    position: sticky;
    top: 0;
    height: 100vh;
  }
}

.text-gradient {
  background: linear-gradient(135deg, #ffffff 0%, #aaaaaa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* MASSIVE Glass Price Card - Redesigned */
.price-glass-card {
  position: relative;
  width: 100%;
  max-width: 260px; /* Deutlich schlanker als vorher (380px) */
  border-radius: 24px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(var(--v-primary-base-rgb), 0.4); /* Nimmt deine Primary-Color auf */
  padding: 20px 16px;
  transform: perspective(1000px) rotateX(5deg);
  transition: transform 0.5s ease, box-shadow 0.5s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5), inset 0 0 15px rgba(var(--v-primary-base-rgb), 0.1);

  &:hover {
    transform: perspective(1000px) rotateX(0deg) translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.6), inset 0 0 25px rgba(var(--v-primary-base-rgb), 0.2);
  }

  .glow-border {
    position: absolute;
    inset: -2px;
    border-radius: 26px;
    background: linear-gradient(135deg, var(--v-primary-base) 0%, transparent 100%);
    z-index: -1;
    opacity: 0.5;
    filter: blur(10px); /* Erzeugt den weichen, äusseren Leuchteffekt */
  }

  .price-content {
    display: flex;
    justify-content: center;
    align-items: center;
    white-space: nowrap;
  }

  .currency-wrapper {
    margin-top: 4px; /* Richtet Icon und CHF optisch an der Zahl aus */
  }

  .currency {
    font-size: 1.1rem;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1;
    letter-spacing: 1px;
  }

  .amount {
    font-size: clamp(3.5rem, 10vw, 5rem); /* Skaliert nicht mehr GANZ so extrem gross */
    font-weight: 900;
    color: #ffffff;
    line-height: 1;
    letter-spacing: -1.5px;
    text-shadow: 0 10px 20px rgba(0, 0, 0, 0.5);
  }
}

/* Mobile Adjustments für die Card */
@media (max-width: 959px) {
  .price-glass-card {
    max-width: 220px; /* Auf Mobile noch kompakter */
    padding: 16px 12px;
    border-radius: 20px;

    .glow-border {
      border-radius: 22px;
    }

    .amount {
      font-size: 3.2rem; /* Feste, kleinere Grösse auf Handys */
    }

    .currency {
      font-size: 0.9rem;
    }

    .currency-wrapper .v-icon {
      font-size: 20px !important; /* Icon auf Mobile minimal kleiner */
    }
  }
}
/* RIGHT PANEL: Content */
.content-panel {
  background: transparent;
  min-height: 100vh;
}

/* Feature List (Icon Items) */
.feature-icon-box {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.conditions-glass {
  background: rgba(0, 0, 0, 0.4);
  border: 1px dashed rgba(255, 255, 255, 0.1);
}

/* Buttons & Animations */
.action-btn {
  transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.2s ease;
  &:hover {
    transform: translateY(-3px) scale(1.02);
  }
}

.pulse-glow {
  box-shadow: 0 0 20px rgba(var(--v-primary-base-rgb), 0.4);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(var(--v-primary-base-rgb), 0.4);
  }
  70% {
    box-shadow: 0 0 0 20px rgba(var(--v-primary-base-rgb), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--v-primary-base-rgb), 0);
  }
}

/* SHARE VIEW */
.success-icon-container {
  position: relative;
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 30px;
  border: 1px solid rgba(var(--v-primary-base-rgb), 0.5);

  .success-glow {
    position: absolute;
    inset: 0;
    border-radius: 30px;
    background: var(--v-primary-base);
    filter: blur(20px);
    opacity: 0.2;
  }
}

.link-copier-glass {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(var(--v-primary-base-rgb), 0.3);
  box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.5);
}

/* Divider Text */
.divider-wrapper {
  display: flex;
  align-items: center;
  text-align: center;
  &::before,
  &::after {
    content: "";
    flex: 1;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
  .divider-text {
    padding: 0 20px;
    font-size: 0.8rem;
  }
}

/* Bento Grid Social Sharing */
.social-bento-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  .bento-item {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    padding: 24px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);

    span {
      color: white;
      font-weight: 600;
      font-size: 0.9rem;
    }

    &:hover {
      transform: translateY(-5px);
    }

    &.whatsapp:hover {
      background: #25d366;
      border-color: #25d366;
    }
    &.telegram:hover {
      background: #0088cc;
      border-color: #0088cc;
    }
    &.facebook:hover {
      background: #1877f2;
      border-color: #1877f2;
    }
    &.email:hover {
      background: #333333;
      border-color: #555;
    }
  }
}

/* Animations */
.scale-fade-enter-active,
.scale-fade-leave-active {
  transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.scale-fade-enter,
.scale-fade-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(20px);
}

/* --- NEU: Mobile Sticky Layout Anpassungen --- */
@media (max-width: 959px) {
  /* Fügt dem Content-Bereich unten Padding hinzu, damit man ganz ans Ende scrollen kann 
     ohne dass der Text von den Sticky-Buttons verdeckt wird */
  .content-panel {
    padding-bottom: 180px !important;
  }

  /* Fixiert die Buttons am unteren Rand mit einem weichen Fade-Hintergrund */
  .mobile-sticky-actions {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 99;
    padding: 24px 24px 16px;
    margin-top: 0 !important;
    background: linear-gradient(to top, rgba(2, 2, 2, 1) 60%, rgba(2, 2, 2, 0.8) 80%, rgba(2, 2, 2, 0) 100%);
    backdrop-filter: blur(8px);

    /* Optional: Falls du einen dezenten Rahmen oben drüber möchtest */
    /* border-top: 1px solid rgba(255, 255, 255, 0.05); */
  }

  /* Share-Ansicht hat auf Mobile im oberen Bereich etwas mehr Luft nach oben,
     da das Hero-Image wegbricht */
  .share-wrapper .pt-md-0 {
    padding-top: 32px !important;
  }
}
</style>
